import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ICustomerUser,
  LabReqTest,
  LabReqField,
  ILabReqDTO,
  ILabReq,
  IAdminTokenPayload
} from '@care-portals/api-interfaces';
import { labReqTemplates } from '@care-portals/data';
import { mapValues, pickBy } from 'lodash';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthFacade } from '../../../+store/auth/auth.facade';
import { LabReqFacade } from '../../../+store/lab-req/lab-req.facade';
import { AppFacade } from '../../../+store/app/app.facade';
import { DynamicFormItemComponent } from '../dynamic-form-item/dynamic-form-item.component';
import { LabRequisitionElementComponent } from '../lab-requisition-element/lab-requisition-element.component';
import { CustomerFacade } from '../../../+store/customer/customer.facade';
import { addDays, addMonths } from 'date-fns';

/** Ontario lab req template: `urgentContactNumber` → `contactNumber` (see lab-req.ts). */
const ONTARIO_DEFAULT_URGENT_CONTACT_NUMBER = '905-291-7970';

/** Ontario “Other tests” iron panel — one line per analyte (replaces combined Iron (...)). */
const ON_IRON_PANEL_TEST_LINES = [
  'Serum iron (Fe)',
  'TIBC',
  '% transferrin saturation',
  'Ferritin'
] as const;

/** TRT1 ON “Other tests” lines (Jack LGV omits standalone Ferritin — listed in ON_IRON_PANEL_TEST_LINES). */
const ON_TRT1_OTHER_TEST_LINES = [
  'Testosterone free and total',
  'B12',
  'Estradiol',
  'Ferritin',
  'LH',
  'FSH',
  'TSH',
  'Prolactin',
  'Lipase',
  'CRP',
  'Total Insulin'
];

function otherTestsRecord(lines: readonly string[]): Record<string, string> {
  return Object.fromEntries(
    lines.map((text, i) => [`otherTests_test${i}`, text])
  ) as Record<string, string>;
}

const defaultONLabReqValues = {
  insuranceType_OHIP: true,
  urgentContactNumber_contactNumber: ONTARIO_DEFAULT_URGENT_CONTACT_NUMBER,
  biochemistry_glucose: true,
  biochemistry_glucose_glucose_fasting: true,
  biochemistry_creatinine: true,
  biochemistry_hbA1C: true,
  biochemistry_sodium: true,
  biochemistry_potassium: true,
  biochemistry_alt: true,
  biochemistry_lipid: true,
  hematology_cbc: true,
  psa_totalPSA: true,
  biochemistry_alk: true,
  psa_insured: true,
  biochemistry_albumin: true,
  otherTests_test: otherTestsRecord(ON_TRT1_OTHER_TEST_LINES)
};
const defaultWLONLabReqValues = {
  insuranceType_OHIP: true,
  urgentContactNumber_contactNumber: ONTARIO_DEFAULT_URGENT_CONTACT_NUMBER,
  biochemistry_glucose: true,
  biochemistry_glucose_glucose_fasting: true,
  biochemistry_creatinine: true,
  biochemistry_hbA1C: true,
  biochemistry_sodium: true,
  biochemistry_potassium: true,
  biochemistry_alt: true,
  biochemistry_lipid: true,
  hematology_cbc: true,
  biochemistry_albumin: true,
  biochemistry_alk: true,
  otherTests_test: otherTestsRecord([
    'B12',
    ...ON_IRON_PANEL_TEST_LINES,
    'TSH',
    'Lipase',
    'CRP',
    'Total Insulin'
  ])
};

const jackLgvOnAdditionalTests = [
  'Uric Acid',
  'Magnesium (Serum)',
  'Lipoprotein(a)',
  'AST',
  'Cortisol (random)',
  ...ON_IRON_PANEL_TEST_LINES,
  'Free T3/Free T4',
  'GGT (Gamma-glutamyl transferase)',
  'Calcium',
  'Serum Phosphate (PO4)',
  'Nucleated RBC (NRBC)'
];

const jillLgvOnAdditionalTests = [
  'Progesterone',
  'Estradiol',
  'LH',
  'FSH',
  'Testosterone (Total)',
  'Prolactin',
  'DHEA-S',
  'Free T3/Free T4',
  'Uric Acid',
  'Magnesium (Serum)',
  'AST',
  'GGT (Gamma-glutamyl transferase)',
  'Lipoprotein(a)',
  'Cortisol (random)',
  'Calcium',
  'Serum Phosphate (PO4)',
  'Nucleated RBC (NRBC)',
  'Apolipoprotein B (ApoB)'
];

const onLgvCheckboxByTest: Record<string, string> = {
  'Uric Acid': 'biochemistry_uricAcid'
};

function withExtraOnOtherTests(
  base: Record<string, any>,
  extraTests: string[]
): Record<string, any> {
  const currentTests = base.otherTests_test ?? {};
  const nextTests = { ...currentTests };
  const startIndex = Object.keys(currentTests).length;
  extraTests.forEach((test, idx) => {
    nextTests[`otherTests_test${startIndex + idx}`] = test;
  });
  return {
    ...base,
    otherTests_test: nextTests
  };
}

function getDefaultLgvOnLabReqValues(
  organization: string,
  customerGender?: string
): Record<string, any> {
  const mapCheckboxesAndCollectUnmapped = (
    tests: string[]
  ): { checkboxValues: Record<string, boolean>; unmappedTests: string[] } => {
    return tests.reduce(
      (acc, test) => {
        const checkboxKey = onLgvCheckboxByTest[test];
        if (checkboxKey !== undefined) {
          acc.checkboxValues[checkboxKey] = true;
        } else {
          acc.unmappedTests.push(test);
        }
        return acc;
      },
      { checkboxValues: {}, unmappedTests: [] as string[] }
    );
  };

  const normalizedGender = customerGender?.toLowerCase?.();
  if (normalizedGender === 'female' || normalizedGender === 'f') {
    const { checkboxValues, unmappedTests } = mapCheckboxesAndCollectUnmapped(
      jillLgvOnAdditionalTests
    );
    return withExtraOnOtherTests(
      { ...defaultWLONLabReqValues, ...checkboxValues },
      unmappedTests
    );
  }
  if (normalizedGender === 'male' || normalizedGender === 'm') {
    const { checkboxValues, unmappedTests } = mapCheckboxesAndCollectUnmapped(
      jackLgvOnAdditionalTests
    );
    return withExtraOnOtherTests(
      {
        ...defaultONLabReqValues,
        otherTests_test: otherTestsRecord(
          ON_TRT1_OTHER_TEST_LINES.filter(line => line !== 'Ferritin')
        ),
        ...checkboxValues,
        psa_totalPSA: false,
        psa_insured: false
      },
      unmappedTests
    );
  }
  if (organization === 'jill_health') {
    const { checkboxValues, unmappedTests } = mapCheckboxesAndCollectUnmapped(
      jillLgvOnAdditionalTests
    );
    return withExtraOnOtherTests(
      { ...defaultWLONLabReqValues, ...checkboxValues },
      unmappedTests
    );
  }
  const { checkboxValues, unmappedTests } = mapCheckboxesAndCollectUnmapped(
    jackLgvOnAdditionalTests
  );
  return withExtraOnOtherTests(
    {
      ...defaultONLabReqValues,
      otherTests_test: otherTestsRecord(
        ON_TRT1_OTHER_TEST_LINES.filter(line => line !== 'Ferritin')
      ),
      ...checkboxValues,
      psa_totalPSA: true,
      psa_insured: true
    },
    unmappedTests
  );
}

@Component({
  selector: 'my-health-admin-lab-requisition-detail',
  templateUrl: './lab-requisition-detail.component.html',
  styleUrls: ['./lab-requisition-detail.component.less']
})
export class LabRequisitionDetailComponent implements OnInit, OnDestroy {
  @ViewChild(DynamicFormItemComponent)
  listItemForm: DynamicFormItemComponent;
  @ViewChildren(LabRequisitionElementComponent)
  elementChildren: QueryList<LabRequisitionElementComponent>;
  @Input() labReq: ILabReq;
  @Input() customer: ICustomerUser;
  @Input() templateId: string;
  user: IAdminTokenPayload;
  labForm!: FormGroup;
  saving: boolean;
  isStart = true;
  labReqTemplates = labReqTemplates;
  tests: Array<LabReqTest>;
  fields: Array<LabReqField> = [];
  elementsData = {};
  organization = '';
  stop$ = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private facade: LabReqFacade,
    public authFacade: AuthFacade,
    private nzDrawerRef: NzDrawerRef,
    private customerFacade: CustomerFacade,
    private appFacade: AppFacade
  ) {
    this.facade.saving$.pipe(takeUntil(this.stop$)).subscribe(res => {
      this.saving = res;
      if (!res && !this.isStart) {
        this.nzDrawerRef.close(true);
      }
    });
    this.authFacade.user$
      .pipe(takeUntil(this.stop$))
      .subscribe(res => (this.user = res));

    this.customerFacade.selectedCustomer$
      .pipe(takeUntil(this.stop$))
      .subscribe(res => (this.customer = res));

    this.appFacade.selectedLayout$
      .pipe(takeUntil(this.stop$))
      .subscribe(layout => {
        this.organization = layout?.organization || '';
      });
  }

  ngOnInit(): void {
    this.facade.selectedLabReq$.pipe(takeUntil(this.stop$)).subscribe(res => {
      this.labReq = res;
      this.isStart = false;
      if (this.labReq) {
        this.templateId = this.labReq.templateId;
      }

      this.tests =
        this.labReqTemplates.find(tpl => tpl.id === this.templateId)?.tests ??
        [];

      this.tests.forEach(test => {
        this.fields.push({
          id: Math.random().toString().slice(2),
          label: test.label,
          type: 'divider'
        });

        test.options.forEach(option => {
          if (option.children || option.elements) {
            this.fields.push({
              id: Math.random().toString().slice(2),
              label: '',
              type: 'break'
            });
          }
          this.fields.push({
            id: test.id + '_' + option.id,
            label: option.label,
            type: option.type,
            required: option.required ?? false,
            elements: option.elements
          });
          if (option.children) {
            this.fields.push({
              id: Math.random().toString().slice(2),
              label: '',
              type: 'break'
            });

            option.children.forEach((child, index) => {
              this.fields.push({
                id: test.id + '_' + option.id + '_' + child.id,
                label: child.label,
                type: child.type,
                required: child.required ?? false,
                level: !index ? 'child' : undefined
              });
            });

            this.fields.push({
              id: Math.random().toString().slice(2),
              label: '',
              type: 'break'
            });
          }
        });
      });

      const groupItems = {};
      this.fields.forEach(field => {
        if (field.type !== 'divider') {
          Object.assign(groupItems, {
            [field.id]: [
              field.type === 'string' ? '' : field.type === 'number' ? 0 : null,
              field.required ? Validators.required : null
            ]
          });
        }
      });

      Object.assign(groupItems, {
        dueDate: ['', Validators.required]
      });

      this.labForm = this.formBuilder.group(groupItems);
      if (this.labReq) {
        this.labForm.patchValue(this.labReq.data);
        if (this.labReq.dueDate) {
          this.labForm.patchValue({ dueDate: this.labReq.dueDate });
        } else {
          this.labForm.patchValue({ dueDate: addDays(new Date(), 30) });
        }
      } else if (this.templateId === 'trt1on') {
        this.labForm.patchValue(defaultONLabReqValues);
        this.labForm.patchValue({ dueDate: addDays(new Date(), 30) });
      } else if (this.templateId === 'wlon') {
        this.labForm.patchValue(defaultWLONLabReqValues);
        this.labForm.patchValue({ dueDate: addDays(new Date(), 30) });
      } else if (this.templateId === 'lgvon') {
        this.labForm.patchValue(
          getDefaultLgvOnLabReqValues(this.organization, this.customer?.gender)
        );
        this.labForm.patchValue({ dueDate: addDays(new Date(), 30) });
      }
    });
  }

  ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.complete();
  }

  public static touchAll(formGroup: FormGroup | FormArray): void {
    mapValues(formGroup.controls, (c, i) => {
      if (c instanceof FormGroup || c instanceof FormArray) {
        this.touchAll(c);
      } else {
        c['markAsDirty']();
        c['updateValueAndValidity']();
      }
    });
  }

  setDueDate(months: number): void {
    this.labForm.patchValue({ dueDate: addMonths(new Date(), months) });
  }

  handleItemsCompleted({ name, value }: { name: string; value: any }) {
    this.labForm.patchValue({ [name]: value });
  }

  handleElementChanged(values: { [key: string]: any }) {
    Object.assign(this.elementsData, { ...values });
  }

  saveLabReq() {
    if (!this.labForm.valid) {
      LabRequisitionDetailComponent.touchAll(this.labForm);
      return;
    }

    if (this.listItemForm) {
      if (!this.listItemForm.checkValidate()) {
        return;
      }

      this.listItemForm.submitForm();
    }

    let elements = this.elementChildren.toArray();
    if (elements && elements.length) {
      for (let i in elements) {
        if (!elements[i].checkValidate()) {
          return;
        }
      }
    }

    const { dueDate, ...formData } = this.labForm.value;

    let value = pickBy(
      { ...this.labReq?.data, ...formData, ...this.elementsData },
      v => v !== null && v !== undefined
    );

    if (!this.labReq) {
      const newLabReq: ILabReqDTO = {
        customer: this.customer._id,
        adminUser: this.user._id,
        templateId: this.templateId,
        data: value,
        dueDate: dueDate
      };

      this.facade.saveLabReq(newLabReq);
    } else {
      const updatedLabReq: ILabReqDTO = {
        adminUser: this.user._id, // TODO: remove this
        data: value,
        dueDate: dueDate
      };

      this.facade.updateLabReq(this.labReq._id, updatedLabReq);
    }
  }

  close(): void {
    this.nzDrawerRef.close(true);
  }
}
